import React, { useState, useEffect, useCallback, useRef } from "react";
import {
    Upload, FileText, RefreshCw, CheckCircle, AlertCircle, Clock,
    ChevronDown, ChevronUp, Download, Play, Zap, Database, X,
    ArrowRight, Package, List, Activity,
} from "lucide-react";
import api from "../../utils/api";
import useCurrentUser from "../../hooks/useCurrentUser";

// ── constants ─────────────────────────────────────────────────────────────────

const ACCEPTED = ".csv,.json,.xml,.edi,.txt";
const FORMATS   = ["csv", "json", "xml", "x12", "edifact"];

const STATUS_CFG = {
    uploaded:  { cls: "bg-slate-100 text-slate-500",   label: "Uploaded" },
    parsed:    { cls: "bg-blue-100 text-blue-700",     label: "Parsed" },
    mapped:    { cls: "bg-violet-100 text-violet-700", label: "Mapped" },
    processed: { cls: "bg-emerald-100 text-emerald-700", label: "Processed" },
    failed:    { cls: "bg-red-100 text-red-600",       label: "Failed" },
};

function Badge({ status }) {
    const cfg = STATUS_CFG[status] || STATUS_CFG.uploaded;
    return <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider ${cfg.cls}`}>{cfg.label}</span>;
}

// ── helpers ───────────────────────────────────────────────────────────────────

function fmtDate(ts) {
    if (!ts) return "—";
    const d = ts.seconds ? new Date(ts.seconds * 1000) : new Date(ts);
    return d.toLocaleString([], { dateStyle: "medium", timeStyle: "short" });
}

function detectFormat(filename, content) {
    const ext = filename.split(".").pop().toLowerCase();
    if (ext === "csv") return "csv";
    if (ext === "json") return "json";
    if (ext === "xml") return "xml";
    if (["edi", "x12"].includes(ext) && content.trimStart().startsWith("ISA")) return "x12";
    if (["edi", "edifact"].includes(ext)) return "edifact";
    if (content.trimStart().startsWith("{") || content.trimStart().startsWith("[")) return "json";
    if (content.trimStart().startsWith("<")) return "xml";
    if (content.trimStart().startsWith("ISA")) return "x12";
    if (content.trimStart().startsWith("UNA") || content.trimStart().startsWith("UNB")) return "edifact";
    return "csv";
}

// ── Upload Panel ──────────────────────────────────────────────────────────────

function UploadPanel({ companyId, onUploaded }) {
    const fileRef = useRef(null);
    const [dragging, setDragging] = useState(false);
    const [uploading, setUploading] = useState(false);
    const [file, setFile] = useState(null);
    const [preview, setPreview] = useState("");
    const [detectedFormat, setDetectedFormat] = useState("");
    const [error, setError] = useState("");

    const processFile = (f) => {
        if (!f) return;
        if (f.size > 524288) { setError("File too large — max 512KB"); return; }
        setFile(f);
        setError("");
        const reader = new FileReader();
        reader.onload = e => {
            const text = e.target.result || "";
            setPreview(text.slice(0, 400));
            setDetectedFormat(detectFormat(f.name, text));
        };
        reader.readAsText(f);
    };

    const handleDrop = (e) => {
        e.preventDefault(); setDragging(false);
        const f = e.dataTransfer.files[0];
        if (f) processFile(f);
    };

    const handleUpload = async () => {
        if (!file) { setError("Select a file first"); return; }
        setUploading(true); setError("");
        try {
            const reader = new FileReader();
            reader.onload = async (e) => {
                const content = e.target.result;
                try {
                    await api.post("/api/edi/upload", {
                        filename: file.name,
                        format: detectedFormat,
                        content,
                        companyId,
                    });
                    setFile(null); setPreview(""); setDetectedFormat("");
                    onUploaded();
                } catch (err) {
                    setError(err.response?.data?.error || "Upload failed");
                } finally {
                    setUploading(false);
                }
            };
            reader.readAsText(file);
        } catch {
            setError("Failed to read file");
            setUploading(false);
        }
    };

    return (
        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-6">
            <h3 className="font-bold text-navy-900 flex items-center gap-2 mb-4">
                <Upload size={18} className="text-violet-500" /> Upload EDI Document
            </h3>

            <div
                onDragOver={e => { e.preventDefault(); setDragging(true); }}
                onDragLeave={() => setDragging(false)}
                onDrop={handleDrop}
                onClick={() => fileRef.current?.click()}
                className={`border-2 border-dashed rounded-2xl p-8 text-center cursor-pointer transition-all ${dragging ? "border-violet-400 bg-violet-50" : "border-slate-200 hover:border-violet-300 hover:bg-slate-50"}`}
            >
                <input ref={fileRef} type="file" accept={ACCEPTED} className="hidden"
                    onChange={e => processFile(e.target.files[0])} />
                <Upload size={32} className="mx-auto text-slate-300 mb-3" />
                <p className="text-sm font-semibold text-slate-500">
                    {file ? file.name : "Drop file here or click to browse"}
                </p>
                <p className="text-[11px] text-slate-400 mt-1">
                    Accepted: CSV, JSON, XML, X12 EDI (.edi), EDIFACT — max 512KB
                </p>
            </div>

            {file && (
                <div className="mt-4 space-y-3">
                    <div className="flex items-center gap-3">
                        <label className="text-xs font-bold text-slate-500 uppercase">Detected Format</label>
                        <select value={detectedFormat} onChange={e => setDetectedFormat(e.target.value)}
                            className="text-xs border border-slate-200 rounded-lg px-2 py-1 focus:outline-none focus:ring-2 focus:ring-violet-300">
                            {FORMATS.map(f => <option key={f} value={f}>{f.toUpperCase()}</option>)}
                        </select>
                    </div>
                    {preview && (
                        <pre className="text-[10px] bg-slate-50 border border-slate-100 rounded-xl p-3 overflow-x-auto max-h-24 text-slate-500 font-mono">
                            {preview}{preview.length >= 400 ? "…" : ""}
                        </pre>
                    )}
                </div>
            )}

            {error && (
                <div className="mt-3 flex items-center gap-2 text-red-600 text-xs">
                    <AlertCircle size={14} /> {error}
                </div>
            )}

            <button onClick={handleUpload} disabled={!file || uploading}
                className="mt-4 w-full py-2.5 bg-navy-900 text-white rounded-xl text-sm font-bold hover:bg-navy-800 disabled:opacity-40 transition-colors">
                {uploading ? "Uploading…" : "Upload Document"}
            </button>
        </div>
    );
}

// ── Document Row ──────────────────────────────────────────────────────────────

function DocumentRow({ doc, onParse, onProcess, onGenerateASN }) {
    const [expanded, setExpanded] = useState(false);
    const [parsing, setParsing] = useState(false);
    const [processing, setProcessing] = useState(false);

    const handleParse = async () => {
        setParsing(true);
        try { await onParse(doc.id); } finally { setParsing(false); }
    };

    const handleProcess = async () => {
        setProcessing(true);
        try { await onProcess(doc.id); } finally { setProcessing(false); }
    };

    return (
        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
            <div className="flex items-center justify-between px-5 py-4">
                <div className="flex items-center gap-3 min-w-0">
                    <button onClick={() => setExpanded(x => !x)} className="text-slate-400 shrink-0">
                        {expanded ? <ChevronUp size={16}/> : <ChevronDown size={16}/>}
                    </button>
                    <FileText size={16} className="text-slate-300 shrink-0" />
                    <div className="min-w-0">
                        <p className="font-semibold text-navy-900 text-sm truncate">{doc.originalFileName}</p>
                        <p className="text-[11px] text-slate-400">
                            {doc.format?.toUpperCase()} · {doc.documentType || "—"} · {fmtDate(doc.createdAt)}
                            {doc.itemCount ? ` · ${doc.itemCount} items` : ""}
                        </p>
                    </div>
                </div>

                <div className="flex items-center gap-2 shrink-0">
                    <Badge status={doc.status} />

                    {doc.status === "uploaded" && (
                        <button onClick={handleParse} disabled={parsing}
                            className="flex items-center gap-1.5 px-3 py-1.5 bg-blue-600 text-white text-xs font-bold rounded-lg hover:bg-blue-700 disabled:opacity-40">
                            <Play size={11}/> {parsing ? "Parsing…" : "Parse"}
                        </button>
                    )}

                    {doc.status === "parsed" && (
                        <button onClick={handleProcess} disabled={processing}
                            className="flex items-center gap-1.5 px-3 py-1.5 bg-emerald-600 text-white text-xs font-bold rounded-lg hover:bg-emerald-700 disabled:opacity-40">
                            <ArrowRight size={11}/> {processing ? "Creating ASN…" : "Create ASN (Draft)"}
                        </button>
                    )}

                    {doc.status === "processed" && doc.relatedEntityId && (
                        <span className="text-[11px] text-emerald-600 font-bold">→ ASN created</span>
                    )}

                    {doc.status === "failed" && (
                        <span className="text-[11px] text-red-500">Parse failed</span>
                    )}
                </div>
            </div>

            {expanded && doc.parsedPayload && (
                <div className="border-t border-slate-100 px-5 py-4 bg-slate-50">
                    <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-3">Parsed Preview</p>
                    {doc.parsedPayload.meta && Object.keys(doc.parsedPayload.meta).filter(k => doc.parsedPayload.meta[k]).length > 0 && (
                        <div className="mb-3 flex flex-wrap gap-x-6 gap-y-1">
                            {Object.entries(doc.parsedPayload.meta).filter(([,v]) => v).map(([k, v]) => (
                                <span key={k} className="text-[11px] text-slate-500">
                                    <span className="font-bold capitalize">{k.replace(/([A-Z])/g, " $1").trim()}:</span> {v}
                                </span>
                            ))}
                        </div>
                    )}
                    <table className="w-full text-xs">
                        <thead>
                            <tr className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">
                                <th className="text-left pb-1.5">SKU</th>
                                <th className="text-left pb-1.5">Description</th>
                                <th className="text-right pb-1.5">Qty</th>
                                <th className="text-right pb-1.5">Unit</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-100">
                            {(doc.parsedPayload.items || []).slice(0, 10).map((item, i) => (
                                <tr key={i}>
                                    <td className="py-1 font-mono text-slate-500">{item.sku || "—"}</td>
                                    <td className="py-1 text-slate-700">{item.description || "—"}</td>
                                    <td className="py-1 text-right text-slate-600">{item.qty}</td>
                                    <td className="py-1 text-right text-slate-400">{item.unit}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                    {(doc.parsedPayload.items || []).length > 10 && (
                        <p className="text-[10px] text-slate-400 mt-2">…and {doc.parsedPayload.items.length - 10} more items</p>
                    )}
                </div>
            )}
        </div>
    );
}

// ── Connections Panel ─────────────────────────────────────────────────────────

function ConnectionsPanel({ connections, onAddConnection, companyId }) {
    const [adding, setAdding] = useState(false);
    const [format, setFormat] = useState("csv");

    const handleAdd = async () => {
        setAdding(true);
        try { await onAddConnection(format); } finally { setAdding(false); }
    };

    return (
        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-6">
            <h3 className="font-bold text-navy-900 flex items-center gap-2 mb-4">
                <Database size={18} className="text-blue-500" /> EDI Connections
            </h3>

            {connections.length === 0 ? (
                <p className="text-sm text-slate-400 mb-4">No connections configured yet. Add one to start importing EDI documents.</p>
            ) : (
                <div className="space-y-2 mb-4">
                    {connections.map(c => (
                        <div key={c.id} className="flex items-center justify-between px-4 py-3 bg-slate-50 rounded-xl border border-slate-100">
                            <div>
                                <span className="text-sm font-bold text-navy-900">{c.format?.toUpperCase()}</span>
                                <span className="ml-2 text-[11px] text-slate-400">{c.provider}</span>
                            </div>
                            <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${c.enabled ? "bg-emerald-100 text-emerald-700" : "bg-slate-100 text-slate-500"}`}>
                                {c.enabled ? "Active" : "Disabled"}
                            </span>
                        </div>
                    ))}
                </div>
            )}

            <div className="flex items-center gap-2">
                <select value={format} onChange={e => setFormat(e.target.value)}
                    className="flex-1 border border-slate-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-300">
                    {FORMATS.map(f => <option key={f} value={f}>{f.toUpperCase()}</option>)}
                </select>
                <button onClick={handleAdd} disabled={adding}
                    className="px-4 py-2 bg-navy-900 text-white rounded-xl text-xs font-bold hover:bg-navy-800 disabled:opacity-40 whitespace-nowrap">
                    {adding ? "Adding…" : "+ Add Connection"}
                </button>
            </div>
        </div>
    );
}

// ── Logs Panel ────────────────────────────────────────────────────────────────

function LogsPanel({ logs }) {
    return (
        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-6">
            <h3 className="font-bold text-navy-900 flex items-center gap-2 mb-4">
                <Activity size={18} className="text-amber-500" /> Sync Logs
            </h3>
            {logs.length === 0 ? (
                <p className="text-sm text-slate-400">No activity yet.</p>
            ) : (
                <div className="space-y-1.5 max-h-72 overflow-y-auto">
                    {logs.map(log => (
                        <div key={log.id} className={`flex items-start gap-2 p-2.5 rounded-xl text-xs ${log.status === "success" ? "bg-emerald-50" : "bg-red-50"}`}>
                            {log.status === "success"
                                ? <CheckCircle size={13} className="text-emerald-500 shrink-0 mt-0.5"/>
                                : <AlertCircle size={13} className="text-red-500 shrink-0 mt-0.5"/>}
                            <div className="min-w-0">
                                <p className="text-slate-700 font-medium truncate">{log.message}</p>
                                {log.error && <p className="text-red-400 text-[10px] mt-0.5">{log.error}</p>}
                                <p className="text-slate-400 text-[10px] mt-0.5">{fmtDate(log.createdAt)}</p>
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}

// ── Generate EDI Panel ────────────────────────────────────────────────────────

function GenerateEDIPanel({ companyId }) {
    const [poId, setPoId] = useState("");
    const [format, setFormat] = useState("x12");
    const [generating, setGenerating] = useState(false);
    const [error, setError] = useState("");

    const handleGenerate = async () => {
        if (!poId.trim()) { setError("Enter a Purchase Order ID"); return; }
        setGenerating(true); setError("");
        try {
            const res = await api.post("/api/edi/generate", { poId: poId.trim(), format }, { responseType: "blob" });
            const url = URL.createObjectURL(new Blob([res.data]));
            const a = document.createElement("a");
            const ext = format === "x12" ? "edi" : "edi";
            a.href = url;
            a.download = `PO_${poId}_${format.toUpperCase()}.${ext}`;
            a.click();
            URL.revokeObjectURL(url);
            setPoId("");
        } catch (e) {
            let msg = "Generation failed";
            if (e.response?.data instanceof Blob) {
                const text = await e.response.data.text();
                try { msg = JSON.parse(text).error || msg; } catch { msg = text || msg; }
            }
            setError(msg);
        } finally {
            setGenerating(false);
        }
    };

    return (
        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-6">
            <h3 className="font-bold text-navy-900 flex items-center gap-2 mb-1">
                <Download size={18} className="text-navy-700" /> Generate Outbound EDI
            </h3>
            <p className="text-xs text-slate-400 mb-4">Convert an approved Purchase Order to EDI 850 / EDIFACT ORDERS for download.</p>

            <div className="space-y-3">
                <div>
                    <label className="text-xs font-bold text-slate-500 uppercase tracking-widest">Purchase Order ID</label>
                    <input value={poId} onChange={e => setPoId(e.target.value)}
                        placeholder="Firestore PO document ID"
                        className="mt-1 w-full border border-slate-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-violet-300"/>
                </div>
                <div>
                    <label className="text-xs font-bold text-slate-500 uppercase tracking-widest">Output Format</label>
                    <select value={format} onChange={e => setFormat(e.target.value)}
                        className="mt-1 w-full border border-slate-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-violet-300">
                        <option value="x12">X12 EDI 850 (ANSI X12)</option>
                        <option value="edifact">EDIFACT ORDERS (UN/EDIFACT D01B)</option>
                    </select>
                </div>
            </div>

            {error && <p className="mt-2 text-xs text-red-500 flex items-center gap-1"><AlertCircle size={12}/>{error}</p>}

            <button onClick={handleGenerate} disabled={generating}
                className="mt-4 w-full py-2.5 bg-navy-900 text-white rounded-xl text-sm font-bold hover:bg-navy-800 disabled:opacity-40 flex items-center justify-center gap-2">
                <Zap size={15}/> {generating ? "Generating…" : "Generate & Download"}
            </button>
        </div>
    );
}

// ── Main ──────────────────────────────────────────────────────────────────────

export default function EDIManagement() {
    const { clientId } = useCurrentUser();
    const [activeTab, setActiveTab] = useState("documents");
    const [documents, setDocuments] = useState([]);
    const [connections, setConnections] = useState([]);
    const [logs, setLogs] = useState([]);
    const [loading, setLoading] = useState(true);
    const [refreshing, setRefreshing] = useState(false);
    const [flash, setFlash] = useState(null);

    const companyId = clientId || "";

    const notify = (msg, type = "success") => {
        setFlash({ msg, type });
        setTimeout(() => setFlash(null), 4000);
    };

    const load = useCallback(async (silent = false) => {
        if (!silent) setLoading(true);
        else setRefreshing(true);
        try {
            const [docsRes, connsRes, logsRes] = await Promise.allSettled([
                api.get("/api/edi/documents?limit=100"),
                api.get("/api/edi/connections"),
                api.get("/api/edi/logs?limit=50"),
            ]);
            if (docsRes.status === "fulfilled")  setDocuments(docsRes.value.data.documents || []);
            if (connsRes.status === "fulfilled") setConnections(connsRes.value.data.connections || []);
            if (logsRes.status === "fulfilled")  setLogs(logsRes.value.data.logs || []);
        } catch {
            // partial failures handled per-request above
        } finally {
            setLoading(false);
            setRefreshing(false);
        }
    }, []);

    useEffect(() => { load(false); }, [load]);

    const handleParse = async (id) => {
        try {
            const res = await api.post(`/api/edi/documents/${id}/parse`);
            notify(`Parsed ${res.data.itemCount} item(s)`);
            load(true);
        } catch (e) {
            notify(e.response?.data?.error || "Parse failed", "error");
        }
    };

    const handleProcess = async (id) => {
        try {
            const res = await api.post(`/api/edi/documents/${id}/process`);
            notify(res.data.message || "ASN draft created");
            load(true);
        } catch (e) {
            notify(e.response?.data?.error || "Process failed", "error");
        }
    };

    const handleAddConnection = async (format) => {
        try {
            await api.post("/api/edi/connections", { format, companyId });
            notify(`${format.toUpperCase()} connection added`);
            load(true);
        } catch (e) {
            notify(e.response?.data?.error || "Failed to add connection", "error");
        }
    };

    const tabs = [
        { id: "documents",    label: "Documents",    icon: FileText },
        { id: "upload",       label: "Upload",        icon: Upload },
        { id: "connections",  label: "Connections",   icon: Database },
        { id: "generate",     label: "Generate EDI",  icon: Download },
        { id: "logs",         label: "Logs",          icon: Activity },
    ];

    // Counts
    const pending = documents.filter(d => d.status === "uploaded").length;
    const parsed  = documents.filter(d => d.status === "parsed").length;
    const done    = documents.filter(d => d.status === "processed").length;

    return (
        <div className="max-w-7xl mx-auto py-8">
            {/* Header */}
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-8">
                <div>
                    <h1 className="text-3xl font-bold text-navy-900 font-serif">EDI Management</h1>
                    <p className="text-slate-500 mt-1 text-sm">
                        Import supplier ASN files · Generate outbound PO documents · Supports CSV, JSON, XML, X12, EDIFACT
                    </p>
                </div>
                <button onClick={() => load(true)} disabled={refreshing}
                    className="p-2 bg-white border border-slate-200 rounded-xl text-slate-400 hover:text-navy-900 transition-all disabled:opacity-40">
                    <RefreshCw size={16} className={refreshing ? "animate-spin" : ""}/>
                </button>
            </div>

            {/* Flash */}
            {flash && (
                <div className={`mb-6 p-3 rounded-xl border flex items-center gap-2 text-sm ${flash.type === "error" ? "bg-red-50 border-red-200 text-red-700" : "bg-emerald-50 border-emerald-200 text-emerald-700"}`}>
                    {flash.type === "error" ? <AlertCircle size={15}/> : <CheckCircle size={15}/>}
                    {flash.msg}
                </div>
            )}

            {/* KPI row */}
            <div className="grid grid-cols-3 gap-4 mb-8">
                {[
                    { label: "Awaiting Parse", value: pending, color: "text-amber-600" },
                    { label: "Ready to Process", value: parsed,  color: "text-blue-600" },
                    { label: "ASNs Created", value: done,    color: "text-emerald-600" },
                ].map(k => (
                    <div key={k.label} className="bg-white rounded-2xl border border-slate-200 shadow-sm p-5 text-center">
                        <div className={`text-3xl font-bold font-serif ${k.color}`}>{k.value}</div>
                        <div className="text-[11px] font-bold text-slate-400 uppercase tracking-widest mt-1">{k.label}</div>
                    </div>
                ))}
            </div>

            {/* Tabs */}
            <div className="flex gap-1 mb-6 bg-slate-100 p-1 rounded-2xl w-fit">
                {tabs.map(t => (
                    <button key={t.id} onClick={() => setActiveTab(t.id)}
                        className={`flex items-center gap-1.5 px-4 py-2 rounded-xl text-xs font-bold transition-all ${activeTab === t.id ? "bg-white text-navy-900 shadow-sm" : "text-slate-500 hover:text-navy-900"}`}>
                        <t.icon size={13}/> {t.label}
                    </button>
                ))}
            </div>

            {/* Documents tab */}
            {activeTab === "documents" && (
                loading ? (
                    <div className="flex items-center gap-3 text-slate-400 py-16 justify-center">
                        <div className="w-5 h-5 border-2 border-slate-200 border-t-blue-500 rounded-full animate-spin"/>
                        <span className="text-sm">Loading documents…</span>
                    </div>
                ) : documents.length === 0 ? (
                    <div className="bg-white rounded-2xl border border-slate-200 p-16 text-center text-slate-400">
                        <Package size={40} className="mx-auto mb-3 text-slate-200"/>
                        <p className="text-sm font-semibold mb-1">No EDI documents yet</p>
                        <p className="text-xs">Upload a CSV, JSON, XML, or EDI file to get started.</p>
                        <button onClick={() => setActiveTab("upload")}
                            className="mt-4 px-4 py-2 bg-navy-900 text-white rounded-xl text-xs font-bold hover:bg-navy-800">
                            Upload Document
                        </button>
                    </div>
                ) : (
                    <div className="space-y-3">
                        {documents.map(doc => (
                            <DocumentRow key={doc.id} doc={doc}
                                onParse={handleParse}
                                onProcess={handleProcess}/>
                        ))}
                    </div>
                )
            )}

            {/* Upload tab */}
            {activeTab === "upload" && (
                <div className="max-w-xl">
                    <UploadPanel companyId={companyId} onUploaded={() => { notify("Document uploaded — go to Documents to parse it"); load(true); setActiveTab("documents"); }}/>
                </div>
            )}

            {/* Connections tab */}
            {activeTab === "connections" && (
                <div className="max-w-xl">
                    <ConnectionsPanel connections={connections} onAddConnection={handleAddConnection} companyId={companyId}/>
                </div>
            )}

            {/* Generate EDI tab */}
            {activeTab === "generate" && (
                <div className="max-w-xl">
                    <GenerateEDIPanel companyId={companyId}/>
                </div>
            )}

            {/* Logs tab */}
            {activeTab === "logs" && (
                <div className="max-w-2xl">
                    <LogsPanel logs={logs}/>
                </div>
            )}
        </div>
    );
}
